import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
// import './Steps.css';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

interface Step4PhotoProps {
  onComplete: (step: string) => void;
}

const MAX_WIDTH = 180;
const MAX_HEIGHT = 180;

const Step4Photo: React.FC<Step4PhotoProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    logger.info('Step4Photo', 'Componente montado');
    return () => logger.info('Step4Photo', 'Componente desmontado');
  }, []);

  const resizeImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calcular las nuevas dimensiones manteniendo el aspect ratio
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round((height * MAX_WIDTH) / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round((width * MAX_HEIGHT) / height);
              height = MAX_HEIGHT;
            }
          }

          // Asegurar que la imagen no sea más grande que el máximo
          width = Math.min(width, MAX_WIDTH);
          height = Math.min(height, MAX_HEIGHT);

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('No se pudo obtener el contexto del canvas'));

          // Dibujar la imagen redimensionada
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Error al redimensionar la imagen'));
            }
          }, 'image/jpeg', 0.8);
        };
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
    });
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        try {
          const resizedImage = await resizeImage(file);
          setPhoto(resizedImage);
          setPreview(URL.createObjectURL(resizedImage));
          setError('');
          logger.info('Step4Photo', 'Imagen procesada y redimensionada', {
            originalSize: file.size,
            newSize: resizedImage.size
          });
        } catch (error) {
          logger.error('Step4Photo', 'Error al procesar la imagen', error);
          setError('Error al procesar la imagen');
        }
      } else {
        setError('Por favor, selecciona un archivo de imagen válido');
        logger.error('Step4Photo', 'Archivo no es imagen', file.type);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    logger.info('Step4Photo', 'Iniciando envío de foto');
    try {
      if (!photo) {
        throw new Error('Por favor, selecciona una foto de perfil');
      }
      if (!auth.currentUser) {
        setError('Usuario no autenticado');
        setLoading(false);
        logger.error('Step4Photo', 'Usuario no autenticado');
        return;
      }
      // Subir la foto a Storage
      const fileRef = ref(storage, `users/${auth.currentUser.uid}/profile/${Date.now()}_${photo.name}`);
      await uploadBytes(fileRef, photo);
      const photoUrl = await getDownloadURL(fileRef);
      // Actualizar el perfil del usuario
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        photoURL: photoUrl,
        updatedAt: new Date().toISOString()
      });
      logger.info('Step4Photo', 'Onboarding de foto completado', { photoUrl });
      onComplete('photo');
    } catch (error: any) {
      logger.error('Step4Photo', 'Error en el proceso', error);
      setError(error.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto p-8 bg-white rounded-[20px_40px_20px_40px] shadow-2xl border border-white/40 animate-fadeIn font-['Montserrat']">
      <h2 className="text-3xl font-bold text-red-600 mb-4 text-center">{t('onboarding.profilePhoto.title')}</h2>
      <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
        {t('onboarding.profilePhoto.description')}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-8 max-w-md mx-auto">
        <div className="flex flex-col items-center gap-6">
          {preview ? (
            <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-gray-200 shadow-lg">
              <img 
                src={preview} 
                alt="Vista previa" 
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-48 h-48 rounded-full border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <p className="text-gray-500 text-sm">{t('onboarding.profilePhoto.dragAndDrop')}</p>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col items-center gap-4">
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="hidden"
            id="photo-upload"
          />
          <label 
            htmlFor="photo-upload" 
            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 cursor-pointer ${
              preview 
                ? 'bg-blue-500 text-white hover:bg-blue-600 hover:-translate-y-0.5 hover:shadow-lg' 
                : 'bg-red-500 text-white hover:bg-red-600 hover:-translate-y-0.5 hover:shadow-lg'
            }`}
          >
            {preview ? t('onboarding.profilePhoto.changePhoto') : t('onboarding.profilePhoto.selectPhoto')}
          </label>
        </div>
        {error && <div className="text-red-600 bg-red-100 border border-red-200 rounded-lg px-4 py-3 text-center">{t(error)}</div>}
        <button 
          type="submit" 
          disabled={loading || !photo}
          className="w-full p-4 bg-red-500 text-white rounded-xl font-semibold transition-all duration-300 hover:bg-red-600 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
        >
          {loading ? t('common.saving') : t('common.saveAndContinue')}
        </button>
      </form>
    </div>
  );
};

export default Step4Photo; 